import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { checkAccess } from "../utils/index";
import { Grid, Typography, Paper, makeStyles, Button, } from '@material-ui/core';
import { withTranslation } from "react-i18next";
import ModalBase from "../utils/ModalBase";
import ToastMessage from "../../Common/ToastMessage";
import { Table } from "../../Common/Table";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { actionShowSpinner } from "../../../redux-flow/action/Wrapper/Spinner";
import {
  Edit,
  Check,
  Clear,
  AddCircleRounded
} from "@material-ui/icons";
import { useAuth } from "../../../context/auth";
import AddNewModalityModal from "./Modals/AddNewModality"
import UpdateModalityModal from "./Modals/UpdateModality"
import { useFormCombos } from "../../../hooks/useFormCombos";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "1vh 1vw",
    width: "100%"
  },
  button: {
    ...theme.typography.subtitle1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    minWidth: 180,
    minHeight: 50,
    borderRadius: 25,
    marginTop: "5vh"
  },
  filterButton: {
    ...theme.typography.subtitle1,
    backgroundColor: '#fff',
    color: '#666666',
    borderRadius: 8,
    border: '1px solid #666666',
    textTransform: "uppercase",
    maxWidth: 140,
    width: '100%',
  },
  spacer: {
    marginBottom: 20
  },
  success: {
    backgroundColor: theme.palette.success.dark,
    borderRadius: 5,
    height: 50
  },
  label: {
    ...theme.typography.subtitle1,
    color: theme.palette.primary.contrastText
  },
  icon: {
    color: theme.palette.primary.contrastText,
    fontSize: 30,
    marginRight: 50
  }
}));

const Modalities = ({ t, actionShowSpinner, history }) => {
  const classes = useStyles();
  const { userAccess } = useAuth();

  const columns = [
    {
      title: t("menu.modalities.link.name").toUpperCase(),
      field: "name",
    },
    {
      title: t("menu.modalities.link.enabled").toUpperCase(),
      field: "enabled",
    },
  ];

  const [updateModalityModalOpened, setUpdateModalityModalOpened] = useState(false);
  const [createModalityModalOpened, setCreateModalityModalOpened] = useState(false);
  const [modalityToEdit, setModalityToEdit] = useState(null);
  const [canUpdateModality, setCanUpdateModality] = useState(false);
  const [canCreateModality, setCanCreateModalities] = useState(false);
  const [toastMessage, setToastMessage] = useState({
    message: '',
    type: 'error',
    open: false,
  });

  const {
    modalityList,
    getAllModalityByLanguageId,
  } = useFormCombos();

  const modalities = useMemo(() => modalityList.map(m => ({
    ...m,
    enabled: !m.disabled ? <Check /> : <Clear />,
    enabledBoolean: !m.disabled,
  })), [modalityList]);

  const loadModalities = useCallback(() => {
    actionShowSpinner(true);

    try {
      const language = localStorage.getItem('i18nextLng');

      if (language) {
        getAllModalityByLanguageId(language, true).then(() => {
          actionShowSpinner(false);
        });
      }
    } catch (error) {
      actionShowSpinner(false);
    }
  }, []);

  useEffect(() => {
    if (userAccess) {
      const hasAccess = checkAccess(userAccess, 'listModalities');
      const hasAccessToCreateModalities = checkAccess(userAccess, 'createModalities');
      const hasAccessToUpdateModalities = checkAccess(userAccess, 'updateModalities');

      if (!hasAccess) {
        history.push('/');
      };

      setCanUpdateModality(hasAccessToUpdateModalities);
      setCanCreateModalities(hasAccessToCreateModalities);

      loadModalities();
    }
  }, [userAccess]);

  const onError = (error) => {
    setToastMessage({
      message: error,
      type: 'error',
      open: true,
    });
  };

  return (
    <Grid container className={classes.root}>
      <ToastMessage
        message={toastMessage.message}
        type={toastMessage.type || 'error'}
        open={toastMessage.open}
        close={() => setToastMessage({ ...toastMessage, open: false })}
      />
      <ModalBase open={createModalityModalOpened} setOpen={setCreateModalityModalOpened}
        content={
          <AddNewModalityModal
            onError={onError}
            t={t}
            setModalStatus={(value) => setCreateModalityModalOpened(value)}
            onSuccess={() => { loadModalities(); }}
          />
        }
      />

      <ModalBase open={updateModalityModalOpened} setOpen={setUpdateModalityModalOpened}
        content={
          <UpdateModalityModal
            onError={onError}
            t={t}
            setModalStatus={(value) => setUpdateModalityModalOpened(value)}
            onSuccess={() => { loadModalities(); }}
            modality={modalityToEdit}
          />
        }
      />

      <Grid item className={classes.spacer}>
        <Typography variant="h4">
          {t("menu.modalities.link.parent")}
        </Typography>
      </Grid>

      <Paper elevation={1} className={classes.root}>
        <Grid container item spacing={3}>
          <Grid item lg={12} xs={12}>
            <Table
              columns={columns}
              data={modalities}
              actions={[
                {
                  icon: () => <Edit />,
                  tooltip: t("edit"),
                  disabled: !canUpdateModality,
                  onClick: (_event, modality) => {
                    setModalityToEdit(modality);
                    setUpdateModalityModalOpened(true);
                  },
                },
              ]}
            />
          </Grid>
        </Grid>
      </Paper>

      <Grid container direction="row" justify="center" alignItems="center">
        <Grid item>
          <Button
            className={classes.button}
            variant="contained"
            startIcon={<AddCircleRounded style={{ fontSize: 30 }} />}
            onClick={() => {
              setCreateModalityModalOpened(true);
            }}
            disabled={!canCreateModality}
          >
            {t("menu.modalities.link.addNewModality")}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
}

const withRouterModalities = withRouter(Modalities);

const mapDispatchToProps = (dispatch) => ({
  actionShowSpinner: (request) => dispatch(actionShowSpinner(request)),
});

const ModalitiesConnect = connect(
  null,
  mapDispatchToProps
)(withRouterModalities);

export default withTranslation()(ModalitiesConnect);